import { useRef } from 'react';
import { useItemSeenBi } from './use-item-seen-bi';
import { ENTITY_TYPE } from './bi-fields';
import { SURVEY_COMPONENT, SURVEY_SHOWN_STEP, surveyOffer } from './survey-bi';
import type { MessageSurvey } from './message-survey';

/**
 * Reports that the assistant offered an inline survey — the impression half of the survey funnel,
 * whose other half is the `clickCta` every step of the card fires (lib/survey-bi). Without it a survey
 * nobody submitted is indistinguishable from one that was never offered, so the submit event has no
 * denominator.
 *
 * `itemSeen` (198:6), NOT `loadFinish` (198:5), and fired from the CARD rather than from the
 * transcript. Both halves of that matter:
 *
 * - A `loadFinish` says a region finished loading and can fire entirely off-screen, so it counts asks
 *   nobody could have read and inflates the denominator of every rate measured against it. This event
 *   answers "was the card put in front of a human", which is a viewport fact — so it rides the
 *   IntersectionObserver `useItemSeenBi` owns.
 * - Observing the rendered TRANSCRIPT counted an ask the moment a row carried one, including asks that
 *   never reached the reader: one that arrived into a channel-locked thread, or that a later turn
 *   superseded. The answerable card lives in ONE place — the composer's slot (MessagesPanel) — so the
 *   card itself is the only thing that knows it was really offered, and it is what reports now.
 *
 * `conversation_id` + `message_id` carry it, and the OPTIONS ride as their harness-minted ids
 * (`surveyOffer`) — NOT the prompts or option labels: those are model-authored from the caller's own
 * context and routinely name their contacts and clients, so shipping them would move third-party
 * personal names into the analytics warehouse as a side effect of counting an event. `message_id`
 * reaches the same text through the message row for anyone entitled to read it.
 *
 * The DEDUP is what the hook is really for. A conversation reopened with an unanswered ask still
 * pending renders the card again, and that is the same OFFER — counting it twice would make the
 * denominator "times a card was drawn" rather than "asks that were made". So an ask is counted once per
 * conversation, in a registry that outlives the card's mount and is kept per thread, so walking away
 * and back does not replay it.
 *
 * The native apps report the same event with the same fields (`SurveyBI.reportShown` /
 * `SurveyBi.reportShown`) from their own stores' arrival points, which already distinguish "this ask
 * just happened" from "this ask is history". Same event, same fields, same rule; only the way of
 * knowing which asks are new differs.
 */

/**
 * Asks already counted, PER CONVERSATION. Module state rather than a ref because it has to survive the
 * card UNMOUNTING — which is exactly what happens between the two renders this is here to tell apart
 * (leaving the chat and coming back to a still-pending ask).
 *
 * Keyed by conversation rather than holding one set beside the conversation it belongs to: a reader who
 * goes A → B → A would otherwise find A's record discarded by the visit to B, and A's still-pending ask
 * counted a second time — the very double count this registry exists to prevent, moved one thread
 * along. Mirrors the native stores' `reportedSurveyShownAskIds`.
 */
const reported = new Map<string, Set<string>>();

/** Test seam: the registry is module state, so a suite that mounts twice has to be able to clear it. */
export function resetSurveyShownBi() {
  reported.clear();
}

export function useSurveyShownBi(
  survey: MessageSurvey | undefined,
  conversationId: string | null | undefined,
  opts?: {
    /** The persisted row's id, when one carries this ask yet. Absent while it is still the turn's early copy. */
    messageId?: string;
    /**
     * Whether this card is the ANSWERABLE one. The same component also draws the settled record of an
     * ask in the transcript, and that is not an offer — it is the memory of one. Left to claim the ask,
     * the record would count the impression and silence the real card, so the record passes `false`.
     */
    enabled?: boolean;
  },
): (node: Element | null) => void {
  const conversation = conversationId ?? undefined;
  const askId = opts?.enabled === false ? undefined : survey?.askId;

  // Whether THIS card is the one that counts, decided ONCE per ask and remembered for the mount. The
  // claim has to happen here rather than in an effect: `useItemSeenBi` owns the observer and fires it
  // itself, so there is no callback to claim the ask from — by the time an effect ran, the event would
  // already be out. The cost is that an ask claimed and then never scrolled into view is not counted at
  // all; the card lives in the composer's slot, which is never off-screen, so that window does not
  // exist in practice.
  const decided = useRef<{ key: string; report: boolean } | undefined>(undefined);
  const thread = conversation ?? '';
  const key = `${thread}|${askId ?? ''}`;
  if (askId && decided.current?.key !== key) {
    let seen = reported.get(thread);
    if (!seen) {
      seen = new Set();
      reported.set(thread, seen);
    }
    const fresh = !seen.has(askId);
    if (fresh) seen.add(askId);
    decided.current = { key, report: fresh };
  }
  const report = !!askId && decided.current?.key === key && decided.current.report;

  return useItemSeenBi(SURVEY_COMPONENT, {
    // The business entity a survey event is about is the CONVERSATION it was offered in — the thing the
    // funnel's other steps carry too, and the only way back to the turn that produced the ask.
    ...(conversation ? { entity: { id: conversation, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: {
      // The key every other step of the funnel carries (lib/survey-bi), so the impression this event
      // records is joinable to the picks, skips, dismissals and submit that followed it — without it the
      // denominator and the numerator are counted in different places and can only be compared in
      // aggregate.
      // The impression's own step name, so web's first funnel step groups with the phones' on the
      // field they all share now that the component name is the card on every event.
      step: SURVEY_SHOWN_STEP,
      ask_id: askId,
      ...(conversation ? { conversation_id: conversation } : {}),
      // The persisted id: the way back to the actual words for anyone entitled to read them. Absent
      // while the ask is still the turn's early copy, which is exactly when no row carries it yet.
      message_id: opts?.messageId,
      ...(survey ? surveyOffer(survey) : {}),
    },
    disabled: !report,
  });
}
